import { zodResolver } from '@hookform/resolvers/zod';
import { useGetTenant, useUpdateTenant } from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sunstone/ui';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { useSlugSync } from '@/hooks/useSlugSync';
import { routes } from '@/lib/routes';
import { displayNameSchema, slugSchema } from '@/lib/schemas';

const schema = z.object({
  name: displayNameSchema,
  slug: slugSchema,
  costCentre: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function EditTenantPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { token } = useAuthToken();

  const { data: tenantData, isLoading } = useGetTenant(tenantId ?? '', token);
  const tenant = tenantData?.tenant;

  const updateTenant = useUpdateTenant(token);
  const { hasPermission } = useAuthorization();
  const isPartner = tenant ? !tenant.parentId : false;
  const editPermission = isPartner ? 'edit:partner' : 'edit:tenant';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { name: '', slug: '', costCentre: '' },
  });

  const { reset } = form;
  useEffect(() => {
    if (tenant) {
      reset({ name: tenant.displayName, slug: tenant.slug, costCentre: tenant.costCentre ?? '' });
    }
  }, [tenant, reset]);

  const syncSlug = useSlugSync(form);

  function onSubmit(values: FormValues) {
    if (!tenantId) return;
    updateTenant.mutate(
      { id: tenantId, displayName: values.name.trim(), slug: values.slug },
      {
        onSuccess: () => {
          navigate(routes.tenant(tenantId));
        },
      },
    );
  }

  if (isLoading || !tenant) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader title={`Editing ${tenant.displayName}`} />

      <Card className="max-w-xl">
        <CardContent className="py-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {updateTenant.isError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {updateTenant.error?.message ?? 'Failed to update.'}
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Allstate Claims"
                        autoFocus
                        onChange={(e) => syncSlug(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      URL Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="allstate-claims" />
                    </FormControl>
                    <FormDescription>Used in URLs and API references.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="costCentre"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost centre</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. CC-1234" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(tenantId ? routes.tenant(tenantId) : routes.customers())}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    !form.formState.isValid ||
                    updateTenant.isPending ||
                    !hasPermission(editPermission)
                  }
                >
                  {updateTenant.isPending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
